/**
 * Diagram Info Button - Handles the disclaimer display for both mobile and desktop
 */

export class DiagramInfoButton {
  constructor() {
    this.disclaimerElement = null;
    this.isExpanded = false;
    this.isInitialized = false;
    this.isMobile = false;
    this.clickOutsideHandler = null;
  }

  /**
   * Initialize the disclaimer
   * @param {HTMLElement} diagramContainer - The diagram container element
   */
  initialize(diagramContainer) {
    if (this.isInitialized) return;
    
    // Check if mobile
    this.checkMobile();
    
    // Create the disclaimer element
    this.createDisclaimer(diagramContainer);
    
    // Set up resize listener
    window.addEventListener('resize', () => this.handleResize());
    
    this.isInitialized = true;
  }
  
  /**
   * Check if we're in mobile view
   */
  checkMobile() {
    this.isMobile = window.innerWidth <= 767;
  }

  /**
   * Create the disclaimer element
   * @param {HTMLElement} container - Container to append the disclaimer to
   */
  createDisclaimer(container) {
    this.disclaimerElement = document.createElement('div');
    this.disclaimerElement.className = 'diagram-disclaimer-element';
    
    if (this.isMobile) {
      // Mobile: Start as button
      this.disclaimerElement.classList.add('mobile', 'collapsed');
      this.disclaimerElement.innerHTML = `
        <button class="disclaimer-button" aria-label="View disclaimer information">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 15H9V13H11V15ZM11 11H9V5H11V11Z" fill="currentColor"/>
          </svg>
        </button>
        <div class="disclaimer-content">
          <p class="text-sm text-gray-500 italic">While we do our best, AI can make mistakes. Don't worry, you're in complete control of the messages that go to your customers.</p>
        </div>
      `;
      
      // Add click handler for mobile
      const button = this.disclaimerElement.querySelector('.disclaimer-button');
      button.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleExpanded();
      });
      
      // Also allow clicking the expanded disclaimer to collapse it
      this.disclaimerElement.addEventListener('click', (e) => {
        if (this.isExpanded && e.target !== button && !button.contains(e.target)) {
          this.collapse();
        }
      });
    } else {
      // Desktop: Always show full disclaimer
      this.disclaimerElement.classList.add('desktop');
      this.disclaimerElement.innerHTML = `
        <p class="text-sm text-gray-500 italic">While we do our best, AI can make mistakes. Don't worry, you're in complete control of the messages that go to your customers.</p>
      `;
    }
    
    container.appendChild(this.disclaimerElement);
  }

  /**
   * Toggle expanded state for mobile
   */
  toggleExpanded() {
    if (!this.isMobile) return;
    
    this.isExpanded = !this.isExpanded;
    
    if (this.isExpanded) {
      this.disclaimerElement.classList.remove('collapsed');
      this.disclaimerElement.classList.add('expanded');
      
      // Add click outside handler with capture to intercept before pan-zoom
      this.clickOutsideHandler = (e) => {
        if (!this.disclaimerElement.contains(e.target)) {
          e.stopPropagation();
          e.preventDefault();
          this.collapse();
        }
      };
      
      // Add handler after a brief delay to prevent immediate closing
      // Use capture phase to get event before other handlers
      setTimeout(() => {
        document.addEventListener('click', this.clickOutsideHandler, true);
        document.addEventListener('touchstart', this.clickOutsideHandler, true);
      }, 100);
    } else {
      this.collapse();
    }
  }
  
  /**
   * Collapse the disclaimer
   */
  collapse() {
    if (!this.isMobile || !this.isExpanded) return;
    
    this.isExpanded = false;
    this.disclaimerElement.classList.remove('expanded');
    this.disclaimerElement.classList.add('collapsed');
    
    // Remove click outside handler
    if (this.clickOutsideHandler) {
      document.removeEventListener('click', this.clickOutsideHandler, true);
      document.removeEventListener('touchstart', this.clickOutsideHandler, true);
      this.clickOutsideHandler = null;
    }
  }
  
  /**
   * Handle window resize
   */
  handleResize() {
    const wasMobile = this.isMobile;
    this.checkMobile();
    
    // If we switched between mobile/desktop, recreate the element
    if (wasMobile !== this.isMobile) {
      const container = this.disclaimerElement.parentElement;
      this.disclaimerElement.remove();
      this.createDisclaimer(container);
    }
  }

  /**
   * Clean up the component
   */
  destroy() {
    if (this.disclaimerElement && this.disclaimerElement.parentNode) {
      this.disclaimerElement.parentNode.removeChild(this.disclaimerElement);
    }
    
    if (this.clickOutsideHandler) {
      document.removeEventListener('click', this.clickOutsideHandler);
    }
    
    window.removeEventListener('resize', () => this.handleResize());
    
    this.disclaimerElement = null;
    this.isExpanded = false;
    this.isInitialized = false;
  }
}

// Export singleton instance
export const diagramInfoButton = new DiagramInfoButton();

// Make available globally for non-module scripts
if (typeof window !== 'undefined') {
  window.diagramInfoButton = diagramInfoButton;
}